// web/src/components/schedule/WeekView.jsx
// Seven day columns, each an agenda grouped by time block — tasks and lessons
// interleaved. Outlook's week, shaped for a barn: the useful axis is the time
// block ("who's doing morning feed"), not a pixel-per-minute grid, because most
// barn work is blocked rather than clocked.
//
// Desktop gets seven columns side by side. On a phone that would be unreadable,
// so the same columns stack — one day after another, today first in view.

import { groupByBlock, itemsForDate, formatDay } from '../../lib/schedule';
import ScheduleItem from './ScheduleItem';

export default function WeekView({
  days,
  today,
  tasks,
  lessons,
  services = [],
  canAdd,
  busy,
  onAddOnDay,
  onOpenItem,
  onOpenDay,
}) {
  return (
    // Seven across only once there's room for seven. At the lg breakpoint a
    // column was 131px, which leaves 57px for a name after the spine, the
    // checkbox and the padding — narrow enough that "Finnegan" broke in half.
    // Below xl the week wraps to two rows instead: a day you can read, twice
    // over, beats seven you can't.
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
      {days.map((date) => (
        <DayColumn
          key={date}
          date={date}
          isToday={date === today}
          items={itemsForDate(date, tasks, lessons, services)}
          canAdd={canAdd}
          busy={busy}
          onAdd={() => onAddOnDay(date)}
          onOpenItem={onOpenItem}
          onOpenDay={onOpenDay}
        />
      ))}
    </div>
  );
}

function DayColumn({ date, isToday, items, canAdd, busy, onAdd, onOpenItem, onOpenDay }) {
  const groups = groupByBlock(items);
  const doneCount = items.filter((i) => i.done).length;

  return (
    <section
      className={
        'card p-2 flex flex-col min-h-[8rem] ' +
        // Hunter, matching the ring on the availability calendar's selected
        // day. Emerald here was a third green, next to the brand green and
        // whatever green a barn had picked for a lesson type.
        (isToday ? 'border-hunter bg-hunter/5' : '')
      }
    >
      <header className="flex items-baseline justify-between px-1 pb-2 mb-1 border-b border-ink-100">
        {/* The heading is the way into that day. It replaced a "Today's board"
            link that went to one fixed day and was the wrong one six times a
            week; a column you are already looking at is the obvious target. */}
        <h3 className="text-sm font-semibold">
          <button
            type="button"
            onClick={() => onOpenDay?.(date)}
            className="hover:underline underline-offset-2"
            aria-label={`Open ${formatDay(date, { weekday: 'long', month: 'long', day: 'numeric' })}`}
          >
            {formatDay(date, { weekday: 'short' })}{' '}
            <span className={isToday ? 'text-hunter' : 'text-ink-400'}>
              {formatDay(date, { day: 'numeric' })}
            </span>
          </button>
        </h3>
        {items.length > 0 && (
          // Every other count in the app carries a word — "5 slots", "2/3
          // booked". A bare "3/10" in a column header is a ratio nobody has
          // been told the units of.
          <span className="text-[11px] text-ink-400">
            <span className="tabular-nums">{doneCount}/{items.length}</span> done
          </span>
        )}
      </header>

      <div className="flex-1 space-y-3">
        {groups.length === 0 ? (
          <p className="px-1 py-3 text-xs text-ink-400">Nothing scheduled.</p>
        ) : (
          groups.map((g) => (
            <div key={g.id}>
              <h4 className="px-1 mb-1 text-[10px] font-medium uppercase tracking-wider text-ink-400">
                {g.label}
              </h4>
              {/* One bordered list with hairline dividers, the same shape the
                  availability day panel uses for its slots — rather than a
                  bordered box per item inside a bordered card, which was two
                  levels of the same object and ate the width the names
                  needed. */}
              <div className="rounded-md border border-ink-200 bg-white divide-y divide-ink-100 overflow-hidden">
                {g.items.map((item) => (
                  <ScheduleItem
                    key={item.key}
                    item={item}
                    busy={busy}
                    onActivate={() => onOpenItem(item)}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {canAdd && (
        <button
          onClick={onAdd}
          className="mt-2 w-full rounded-lg border border-dashed border-ink-200 py-1.5 text-xs text-ink-400 hover:text-ink-700 hover:border-ink-300"
          aria-label={`Add to ${formatDay(date, { weekday: 'long', month: 'short', day: 'numeric' })}`}
        >
          + Add
        </button>
      )}
    </section>
  );
}
